import { EntityTypeAccessSection } from "@/features/entity-types/components/entity-type-access-section";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import {
	useDisableAssetType,
	useDisableAssetTypeSubOrganization,
	useEnableAssetType,
	useEnableAssetTypeSubOrganization,
} from "@/services/asset/asset.mutations";
import { useAssetTypeDrawerContext } from "./asset-type-drawer-context";

interface AssetTypeAccessSectionProps {
	orgId: string;
}

export function AssetTypeAccessSection({ orgId }: AssetTypeAccessSectionProps) {
	const { t } = useAppTranslation();
	const { assetType, setDraftSubOrgIds } = useAssetTypeDrawerContext();
	const accessEdit = useTypeAccessEditAllowed("AssetType", assetType.id);
	const enableType = useEnableAssetType();
	const disableType = useDisableAssetType();
	const enableSubOrg = useEnableAssetTypeSubOrganization();
	const disableSubOrg = useDisableAssetTypeSubOrganization();
	const grantedIds = (assetType.assetTypeSubOrganizations ?? [])
		.map((row) => row.id)
		.filter((id): id is string => id !== null);

	return (
		<EntityTypeAccessSection
			orgId={orgId}
			typeEnabled={assetType.enabled !== false}
			grantedIds={grantedIds}
			setDraftSubOrgIds={setDraftSubOrgIds}
			canEdit={accessEdit.allowed && !accessEdit.isPending}
			entityLabel={t("admin.entityTypes.access.entity.asset")}
			enabledLabel={t("admin.assetTypes.drawer.access.enabled")}
			disabledLabel={t("admin.assetTypes.drawer.access.disabled")}
			switchTestId="asset-type-org-enable-switch"
			tableKey="web.org-admin.asset-type.access"
			emptyTitle={t("admin.assetTypes.drawer.access.subOrgs.empty", {
				subOrganization: t("subOrganization.entityLabel"),
			})}
			grantSubOrg={(subOrgId) =>
				enableSubOrg.mutateAsync({
					assetTypeId: assetType.id,
					subOrgId,
					organizationId: orgId,
				})
			}
			revokeSubOrg={(subOrgId) =>
				disableSubOrg.mutateAsync({
					assetTypeId: assetType.id,
					subOrgId,
					organizationId: orgId,
				})
			}
			enableOrganization={() =>
				enableType.mutateAsync({
					assetTypeId: assetType.id,
					organizationId: orgId,
				})
			}
			disableOrganization={() =>
				disableType.mutateAsync({
					assetTypeId: assetType.id,
					organizationId: orgId,
				})
			}
		/>
	);
}
